import { GiftAddressRegion } from '@shared/api/common/giftAddressRegion.interface';

import { GiftItemForStore } from '../giftItem/giftItem.interface';
import { Image } from '../media/media.interface';

export enum GiftDeliveryShareStatus {
  NotShared = 'NotShared',
  Shared = 'Shared',
  Viewed = 'Viewed',
}

export enum GiftDeliveryStatus {
  WaitingAddress = 'WaitingAddress', // 배송지 입력 대기 (Individual)
  AddressSubmitted = 'AddressSubmitted', // 배송지 입력 완료
  Preparing = 'Preparing', // 상품 준비중
  Shipping = 'Shipping', // 배송중
  Delivered = 'Delivered', // 배송 완료
  Canceled = 'Canceled', // 취소
}

export enum GiftDeliveryType {
  Individual = 'Individual',
  Bulk = 'Bulk',
}

/**
 * 공유 매체 (마지막으로 공유한 채널 / shareHistory에 함께 기록)
 * gift-coupon의 ShareChannel과 동일한 값셋이지만 백엔드에서 별도 enum으로 관리됨
 */
export enum GiftDeliveryShareChannel {
  KakaoTalk = 'KakaoTalk',
  SMS = 'SMS',
  Link = 'Link',
  Other = 'Other',
}

export interface GiftDeliveryRecipient extends GiftAddressRegion {
  name: string;
  phoneNumber: string;
  address: string;
  addressDetail: string;
  postalCode: string;
  submittedAt: Date;
}

export interface GiftDeliveryShipping {
  trackingNumber: string;
  carrier: string;
  shippedAt: Date;
  deliveredAt: Date;
  deliveredImage?: string | { _id: string; url: string };
}

/**
 * 같은 주문 내 다른 배송 중 동일 카톡방(hashChatId) 으로 전달된 기록.
 * 백엔드가 카톡 공유 웹훅 수신 시 자동 갱신한다.
 *
 * UI 상 "같은 분께 또 보냄" 안내 노출 조건:
 *   `delivery.lastSharedKakaoHashChatId === entry.hashChatId`
 * 인 항목이 1건 이상 있어야 함.
 */
export interface DuplicateRecipientDeliveryEntry {
  hashChatId: string;
  /** 충돌난 배송의 ObjectId */
  delivery: string;
  /** 충돌난 배송의 code */
  code: string;
  /** 충돌난 배송의 순번(#N번째) */
  seq: number;
}

export interface GiftDeliveryPublicResponse {
  _id: string;
  code: string;
  seq: number;
  fullCode: string;
  type: GiftDeliveryType;
  quantity: number;
  shareStatus: GiftDeliveryShareStatus;
  deliveryStatus: GiftDeliveryStatus;
  sharedAt?: Date;
  viewedAt?: Date;
  /** 카카오톡 공유 웹훅으로 확인된 실제 전달 시각 */
  kakaoSharedAt?: Date;
  /** 카카오톡 공유 웹훅으로 집계된 서로 다른 카톡방(채팅) 수 */
  shareKakaoCount?: number;
  /** 가장 마지막 카카오 공유 웹훅의 hashChatId (다건수신 판정용) */
  lastSharedKakaoHashChatId?: string;
  /** 같은 주문 내 동일 카톡방으로 전달된 다른 배송들 (다건수신 판정용) */
  duplicateRecipientDeliveries?: DuplicateRecipientDeliveryEntry[];
  /** 마지막으로 공유한 매체 */
  lastSharedChannel?: GiftDeliveryShareChannel;
  purchasedAt: Date;
  expiresAt: Date;
  recipient?: GiftDeliveryRecipient;
  shipping?: GiftDeliveryShipping;
  giftItem: {
    name: string;
    image: Image;
    giftItemInfo: {
      name: string;
      image: Image;
      detailInfo?: string;
    };
  };
  giftOrder: {
    courtesyContent?: string;
    receiverName?: string;
    receiverPhoneNumber?: string;
  };
}

// Store에서 사용하는 타입 (주문 상세 페이지에서 deliveries virtual로 가져올 때)
export interface GiftDeliveryForStore {
  _id: string;
  code: string;
  seq: number;
  fullCode: string;
  type: GiftDeliveryType;
  quantity: number;
  shareStatus: GiftDeliveryShareStatus;
  deliveryStatus: GiftDeliveryStatus;
  sharedAt?: Date;
  viewedAt?: Date;
  confirmedAt?: Date;
  /** 카카오톡 공유 웹훅으로 확인된 실제 전달 시각 */
  kakaoSharedAt?: Date;
  /** 카카오톡 공유 웹훅으로 집계된 서로 다른 카톡방(채팅) 수 */
  shareKakaoCount?: number;
  /** 가장 마지막 카카오 공유 웹훅의 hashChatId (다건수신 판정용) */
  lastSharedKakaoHashChatId?: string;
  /** 같은 주문 내 동일 카톡방으로 전달된 다른 배송들 (다건수신 판정용) */
  duplicateRecipientDeliveries?: DuplicateRecipientDeliveryEntry[];
  /** 마지막으로 공유한 매체 */
  lastSharedChannel?: GiftDeliveryShareChannel;
  memo?: string;
  /**
   * 회수(잘못 보낸 케이스) 시 잠근 수령자 휴대폰번호. 등록되어 있으면 이 번호로만
   * 본인 인증이 가능하다.
   */
  additionalRecipientPhone?: string;
  purchasedAt: Date;
  expiresAt: Date;
  recipient?: GiftDeliveryRecipient;
  shipping?: GiftDeliveryShipping;
  /** 배송 희망일 'YYYY-MM-DD'(KST) — 날짜 지정 브랜드 주문에서 주문 값 스냅샷. 구서버·일반 브랜드엔 없음 */
  desiredDeliveryDate?: string;
  giftItem: GiftItemForStore | string;
  giftOrder: string;
}

// 배송지 입력 DTO
export interface GiftDeliveryAddressDto {
  name: string;
  phoneNumber: string;
  address: string;
  addressDetail?: string;
  postalCode: string;
}

// 주문 목록에서 사용하는 간소화 배송 타입 (virtual populate select)
export interface GiftDeliverySummary {
  _id: string;
  code: string;
  seq: number;
  type: GiftDeliveryType;
  quantity: number;
  shareStatus: GiftDeliveryShareStatus;
  deliveryStatus: GiftDeliveryStatus;
  sharedAt?: Date;
  viewedAt?: Date;
  /** 카카오톡 공유 웹훅으로 확인된 실제 전달 시각 */
  kakaoSharedAt?: Date;
  /** 카카오톡 공유 웹훅으로 집계된 서로 다른 카톡방(채팅) 수 */
  shareKakaoCount?: number;
  /** 가장 마지막 카카오 공유 웹훅의 hashChatId (다건수신 판정용) */
  lastSharedKakaoHashChatId?: string;
  /** 같은 주문 내 동일 카톡방으로 전달된 다른 배송들 (다건수신 판정용) */
  duplicateRecipientDeliveries?: DuplicateRecipientDeliveryEntry[];
  /** 마지막으로 공유한 매체 */
  lastSharedChannel?: GiftDeliveryShareChannel;
  recipient?: {
    name: string;
    phoneNumber: string;
    submittedAt: Date;
  };
  /**
   * 카카오톡 공유 웹훅 역참조 (virtual). 백엔드가 populate 시 채워서 내려준다.
   * 같은 배송이 여러 채팅방에 공유되었을 경우 콜백이 여러 건 누적된다.
   */
  kakaoShareCallbacks?: KakaoShareCallback[];
}

// Admin용 배송 관리 타입 (giftOrder, giftItem populate)
export interface GiftDeliveryOrderInfo {
  _id: string;
  receiverName?: string;
  receiverPhoneNumber?: string;
  deliveryMode?: string;
  paymentDetail?: {
    purchasedAt?: Date;
  };
  bugo?: {
    _id: string;
    member?: {
      _id: string;
      fullName: string;
      phoneNumber: string;
    };
  };
}

/** 배송관리에서 받는 날을 수정한 기록 한 건 */
export interface GiftDesiredDeliveryDateLog {
  /** 바꾸기 전 날짜 'YYYY-MM-DD' (지정이 없던 건에 새로 넣었으면 null) */
  from?: string | null;
  /** 바꾼 날짜 'YYYY-MM-DD' */
  to: string;
  at: Date;
  /** 누가 바꿨는지 — 'admin' | 'gift-brand-seller' */
  byRole?: string;
  /** 규칙(마감·휴무·선택 기간) 밖 날짜를 비밀번호로 강제 지정한 건 */
  forced?: boolean;
}

export interface GiftDeliveryForAdmin {
  _id: string;
  code: string;
  seq: number;
  fullCode: string;
  type: GiftDeliveryType;
  quantity: number;
  /**
   * 구매 시점 동결 단가당 수수료(원, 정수 — giftItemEmbed.feePerUnit denormalize).
   * 있으면 수수료 계산 없이 이 값을 그대로 차감 (2026-07-24 이후 생성분, 레거시엔 없음).
   */
  saleFeeCharge?: number;
  shareStatus: GiftDeliveryShareStatus;
  deliveryStatus: GiftDeliveryStatus;
  sharedAt?: Date;
  viewedAt?: Date;
  /** 카카오톡 공유 웹훅으로 확인된 실제 전달 시각 */
  kakaoSharedAt?: Date;
  /** 카카오톡 공유 웹훅으로 집계된 서로 다른 카톡방(채팅) 수 */
  shareKakaoCount?: number;
  /** 마지막으로 공유한 매체 */
  lastSharedChannel?: GiftDeliveryShareChannel;
  purchasedAt: Date;
  expiresAt: Date;
  recipient?: GiftDeliveryRecipient;
  shipping?: GiftDeliveryShipping;
  /** 배송 희망일 'YYYY-MM-DD'(KST) — 날짜 지정 브랜드 주문에서 주문 값 스냅샷. 구서버·일반 브랜드엔 없음 */
  desiredDeliveryDate?: string;
  /** 결제(입금)가 기한을 넘겨 받는 날이 자동 변경된 건 — desiredDeliveryDate는 이미 바뀐 날짜다 */
  desiredDeliveryDateLate?: boolean;
  /** 바뀌기 전(구매자가 처음 고른) 날짜 'YYYY-MM-DD' — 자동 변경된 건에만 */
  desiredDeliveryDateRequested?: string;
  /** 배송관리에서 받는 날을 손으로 바꾼 이력 (오래된 순). 수정한 적 없으면 없음 */
  desiredDeliveryDateLogs?: GiftDesiredDeliveryDateLog[];
  /**
   * 주문 시 선택한 추가옵션 스냅샷 (출고 시 어떤 옵션 상품을 보낼지 확인용).
   * selectionCount>1이면 슬롯마다 1개씩 (slotIndex로 구분).
   */
  selectedOptions?: {
    optionName: string;
    slotIndex?: number;
    choiceName: string;
    priceDelta: number;
  }[];
  /** 주문 시 작성된 감사카드 스냅샷 (templateId로 브랜드 템플릿 레이아웃 매칭) */
  thanksPrint?: {
    templateId: string;
    message: string;
    senderName: string;
  };
  /** 소속 브랜드 (감사카드 템플릿 조회용 / admin 조회 시 name populate) */
  giftItemBrand?: string | { _id: string; name: string };
  giftItem: GiftItemForStore | string;
  giftOrder: GiftDeliveryOrderInfo | string;
  createdAt: Date;
  updatedAt: Date;
}

/**
 * /settlement-pending 응답에서 실제 populate되는 delivery 타입.
 * GiftDeliveryForAdmin보다 giftItem/giftOrder 필드가 더 풍부하다.
 */
export interface GiftDeliveryForRebatePending {
  _id: string;
  code: string;
  seq: number;
  quantity: number;
  /**
   * 구매 시점 동결 단가당 수수료(원, 정수 — giftItemEmbed.feePerUnit denormalize).
   * 있으면 수수료 계산 없이 이 값을 그대로 차감 (2026-07-24 이후 생성분, 레거시엔 없음).
   */
  saleFeeCharge?: number;
  deliveryStatus: GiftDeliveryStatus;
  purchasedAt: Date;
  expiresAt: Date;
  recipient?: GiftDeliveryRecipient;
  shipping?: GiftDeliveryShipping;
  giftItemBrand?: {
    _id: string;
    name: string;
    settlementInfo?: {
      accountEntity?: string;
      feeRate?: number;
      /** 소량 개당 추가단가 (수량 < freeShippingMinQuantity 일 때 개당 가산) */
      shippingFee?: number;
      /** 이 수량 이상이면 소량 추가단가 미적용 */
      freeShippingMinQuantity?: number;
    };
  };
  giftItem?: {
    _id: string;
    name: string;
    priceRetail: number;
    priceWhole: number;
    priceRebate?: number;
    giftItemInfo?: {
      _id: string;
      name: string;
      priceWhole: number;
    };
    bugoBrand?: {
      _id: string;
      giftAccountConfig?: {
        enabled: boolean;
        viaMari?: boolean;
        method?: 'FeeRate' | 'Rebate';
        feeRate?: number;
      };
    };
  };
  giftOrder?: {
    _id: string;
    receiverName?: string;
    receiverPhoneNumber?: string;
    bugoBrand?: string;
    /** 부고 → 장례 이벤트(fevent): 고인명·발인일·지도사 표시용 (populate 시에만 존재) */
    bugo?: {
      _id: string;
      fevent?: {
        _id: string;
        deceasedInfo?: {
          name?: string;
          coffinOut?: { date?: Date };
        };
        /** 담당 지도사(장례지도사) */
        user?: {
          _id: string;
          info?: { name?: string; phoneNumber?: string };
        };
      };
    };
    orderDetails?: {
      giftItem: string;
      giftItemEmbed?: {
        name?: string;
        priceRetail?: number;
        priceWhole?: number;
        priceRebate?: number;
        /** 구매 당시 수수료율(%) 스냅샷 (FeeRate 상조, 2026-07-24 이후) */
        feeRate?: number;
        /** 구매 시점 동결 단가당 수수료(원, 1원 올림 정수) — saleFeeCharge의 원본 */
        feePerUnit?: number;
      };
    }[];
  };
  createdAt: Date;
  updatedAt: Date;
}

// Admin 배송 대시보드 응답
export interface DeliveryDashboardData {
  waitingAddress: GiftDeliveryForAdmin[];
  addressSubmitted: GiftDeliveryForAdmin[];
  preparing: GiftDeliveryForAdmin[];
  shipping: GiftDeliveryForAdmin[];
  todayDelivered: GiftDeliveryForAdmin[];
}

// Admin 배송 통계
export interface GiftDeliveryStatistics {
  waitingAddress: number;
  addressSubmitted: number;
  preparing: number;
  shipping: number;
  delivered: number;
  total: number;
}

// 카카오톡 공유 웹훅 콜백
// args.shareContent === 'GiftCoupon' | 'GiftDelivery' 로 구분되며
// 각각 args.couponId / args.deliveryId, args.couponCode, args.domainUrl 를 포함합니다.
export interface KakaoShareCallback {
  _id: string;
  chatType: string;
  hashChatId: string;
  templateId: number;
  args: {
    shareContent?: 'GiftCoupon' | 'GiftDelivery';
    couponId?: string;
    deliveryId?: string;
    couponCode?: string;
    domainUrl?: string;
    [key: string]: string | undefined;
  };
  /** 백엔드에서 args.couponId 를 별도 필드로 노출 (조건부) */
  couponId?: string;
  /** 백엔드에서 args.deliveryId 를 별도 필드로 노출 (조건부) */
  deliveryId?: string;
  appName?: string;
  kakaoResourceId?: string;
  createdAt: string;
  updatedAt: string;
}

// Admin 배송 검색 파라미터
export interface GiftDeliverySearchParams {
  deliveryStatus?: GiftDeliveryStatus;
  type?: GiftDeliveryType;
  recipientName?: string;
  recipientPhone?: string;
  ordererName?: string;
  dateFrom?: string;
  dateTo?: string;
}
